import { useEffect } from 'react';

/**
 * Re-initialises Yotpo widgets once the Yotpo loader script is ready.
 *
 * The site runs Yotpo's current widget generation, which exposes
 * `window.yotpoWidgetsContainer.initWidgets()` — the legacy `window.yotpo`
 * instance is never created. The loader script (injected in app/layout.js) is
 * async, so on a fresh page load the container may not exist yet: this polls
 * until it is ready, then initialises. On client-side navigation the container
 * already exists, so it fires on the first attempt — which is what makes the
 * widgets show up without a manual refresh.
 *
 * @param {boolean} [enabled=true] - skip when the widget is not rendered
 */
export function useYotpoRefresh(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let attempts = 0;
    let timerId;
    const MAX_ATTEMPTS = 50; // ~10s at 200ms intervals

    const init = () => {
      if (cancelled) return;

      if (typeof window.yotpoWidgetsContainer?.initWidgets === 'function') {
        try {
          window.yotpoWidgetsContainer.initWidgets();
        } catch {
          // Container present but not ready in the expected shape — stop.
        }
        return;
      }

      if (attempts++ < MAX_ATTEMPTS) {
        timerId = setTimeout(init, 200);
      }
    };

    init();

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [enabled]);
}
